import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { useProject } from './useProject';
import { Blip, MobileFloatingBottomRightAction, PageContent7x3, PageMainColumn, PageSecondaryColumn, PageTitle, PageTitleLine, PageWrapper, TogglePseudolink } from '../Common/PageComponents';
import { Buttons, LoadingIndicator, PseudoLink2, useDialogState, useSaveable, useWindowHotkey } from '../../toolympus/components/primitives';
import { SpellbookEditor, DocumentTitle, CurrentDocumentProvider } from '../Documents';
import { ProjectTasksList } from '../Tasks/ProjectTasksList';
import { ProjectResolvedTasksList } from '../Tasks/ProjectResolvedTasksList';
import { ProjectStatus } from './ProjectStatus';
import { ProjectTagsPicker } from './ProjectTagsPicker';
import { StrippedIconButton } from '../../toolympus/components/primitives/StrippedButtons';
import { Save, Close } from '@mui/icons-material';
import { ProjectTasksExportDialog } from './ProjectTasksExportDialog';
import isHotkey from 'is-hotkey';

interface Props {
  
}

const MobileTabToggles = styled(Buttons)`
  display: none;

  ${props => props.theme.breakpoints.down("sm")} {
    display: flex;
    flex-flow: row;
    align-self: flex-start;
  }
`;

const TaskFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-left: 8px;
  border: 1px solid var(--colors-border-main, #ccc);
  border-radius: 4px;

  & input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    padding: 8px 0;
    font-size: 14px;
  }
`;

export const ProjectPage = (props: Props) => {
  const { id } = useParams<{ id: string }>();
  const data = useProject(id);
  const { attributes, description } = data;

  const [whichTaskList,setWhichTaskList] = useState<"current" | "resolved">("current");

  const [mobileTab, setMobileTab] = useState<string>("description");
  
  // Состояния для фильтра задач
  const [taskFilter, setTaskFilter] = useState<string>("");
  const [isTaskFilterVisible, setIsTaskFilterVisible] = useState<boolean>(false);

  useSaveable(data);

  const exportTasksDialog = useDialogState();

  // Хоткей для переключения видимости фильтра
  useWindowHotkey("alt+w", () => {
    setIsTaskFilterVisible(prev => !prev);
  });

  const clearAndCloseFilter = () => {
    setTaskFilter("");
    setIsTaskFilterVisible(false);
  };

  return (
    <PageWrapper>
      <PageTitleLine>
        <PageTitle>
          <DocumentTitle
            value={attributes.data.title}
            update={v => attributes.update({ title: v })}
            />
        </PageTitle>
        {(attributes.isLoading || description.isLoading || description.isSaving) && <LoadingIndicator />}
        {data.hasChanges && <Blip onClick={() => data.save()} />}
        
        <button className="secondary-action-button" onClick={() => exportTasksDialog.open()}>
          экспорт задач
        </button>
        <ProjectStatus
          project={data.attributes.data}
          update={c => data.attributes.save(c)}
          />
      </PageTitleLine>

      <PageContent7x3>
        <MobileTabToggles>
          <TogglePseudolink border isInactive={mobileTab !== "description"} onClick={() => setMobileTab("description")}>описание</TogglePseudolink>
          <TogglePseudolink border isInactive={mobileTab !== "tasks"} onClick={() => setMobileTab("tasks")}>задачи</TogglePseudolink>
        </MobileTabToggles>

        <PageMainColumn isMobileHidden={mobileTab !== "description"}>
          <ProjectTagsPicker
            tags={data.attributes.data.tags}
            update={v => data.attributes.update({ tags: v })}
            />

          <CurrentDocumentProvider document={data.description.data}>
            {description.data._id &&
              <SpellbookEditor
                key={description.data._id}
                content={description.data.content}
                update={v => description.update({ content: v })}
                />}
          </CurrentDocumentProvider>

          <MobileFloatingBottomRightAction isVisible={data.hasChanges}>
            <StrippedIconButton onClick={() => data.save()}><Save /></StrippedIconButton>
          </MobileFloatingBottomRightAction>
        </PageMainColumn>

        <PageSecondaryColumn noBackground isMobileHidden={mobileTab !== "tasks"}>          
          {/* Поле фильтра задач */}
          {isTaskFilterVisible && (
            <TaskFilterWrapper>
              <input
                autoFocus
                placeholder="Фильтр задач..."
                value={taskFilter}
                onChange={e => setTaskFilter(e.target.value)}
                onKeyDown={e => {
                  if (isHotkey("esc", e)) {
                    e.preventDefault();
                    clearAndCloseFilter();
                  }
                }}
              />
              <StrippedIconButton onClick={clearAndCloseFilter}>
                <Close fontSize="small" />
              </StrippedIconButton>
            </TaskFilterWrapper>
          )}

          {whichTaskList === "current" &&
            <ProjectTasksList
              projectId={attributes.data.project_id}
              filter={taskFilter}
              headerButtons={<>
                <PseudoLink2 onClick={() => setIsTaskFilterVisible(true)}>🔎</PseudoLink2>
                <PseudoLink2 onClick={() => setWhichTaskList("resolved")}>завершенные</PseudoLink2>
              </>
              }
              />}

          {whichTaskList === "resolved" &&
            <ProjectResolvedTasksList
              projectId={attributes.data.project_id}
              filter={taskFilter}
              headerButtons={<>
                <PseudoLink2 onClick={() => setIsTaskFilterVisible(true)}>🔎</PseudoLink2>
                <PseudoLink2 onClick={() => setWhichTaskList("current")}>текущие</PseudoLink2>
              </>
              }
              />}
        </PageSecondaryColumn>
      </PageContent7x3>

      {!!exportTasksDialog.isOpen &&
        <ProjectTasksExportDialog
          projectId={attributes.data.project_id}
          {...exportTasksDialog}
          />}
    </PageWrapper>
  );
}